import {
  assertInInjectionContext,
  DestroyRef,
  effect,
  inject,
  Injector,
  Signal,
  Type,
  untracked,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isObservable, Observable, Subject } from 'rxjs';
import { Prettify } from '../types/prettify';

type EntryBindingSource<Value> = Signal<Value> | Observable<Value>;
type EntryTarget = WritableSignal<unknown> | Subject<unknown>;
type SignalResolvedBinding = {
  bindingSource: Signal<unknown>;
  serviceEntry: EntryTarget;
};
type ResolvedBinding = {
  bindingSource: EntryBindingSource<unknown>;
  serviceEntry: EntryTarget;
};
type EntryValue<Entry> =
  Entry extends WritableSignal<infer Value>
    ? Value
    : Entry extends Subject<infer Value>
      ? Value
      : never;

type EntryBindings<Service> = Prettify<
  Partial<{
    [
      K in keyof Service as K extends `${infer EntryKey}Entry`
        ? EntryValue<Service[K]> extends never
          ? never
          : EntryKey
        : never
    ]: EntryBindingSource<EntryValue<Service[K]>>;
  }>
>;

export type ToInjectBindings<Service> = EntryBindings<Service>;

/**
 * Creates an Angular `inject()` helper bound to a service and keeps selected
 * `...Entry` writable signals synchronized with external signals.
 *
 * Service properties ending with `Entry` are exposed as configuration keys
 * without the suffix.
 *
 * @example
 * ```ts
 * @Injectable({ providedIn: 'root' })
 * class MyService {
 *   myIdEntry = signal<string | undefined>(undefined);
 * }
 *
 * const injectMyService = toInject(MyService);
 *
 * @Component({})
 * class MyComponent {
 *   id = input<string>();
 *   myService = injectMyService({ myId: this.id });
 * }
 * ```
 */
export function toInject<Service>(
  token: Type<Service>,
): (bindings?: ToInjectBindings<Service>) => Service {
  function injectWithBindings(bindings?: ToInjectBindings<Service>) {
    assertInInjectionContext(injectWithBindings);

    const injector = inject(Injector);
    const destroyRef = inject(DestroyRef);
    const service = inject(token);
    const resolvedBindings: ResolvedBinding[] = Object.entries(bindings ?? {}).flatMap(
      ([bindingKey, bindingSource]) => {
        if (!bindingSource) {
          return [];
        }

        const serviceEntry = (service as Record<string, unknown>)[`${bindingKey}Entry`];

        if (!isEntryTarget(serviceEntry)) {
          throw new Error(
            `toInject: "${bindingKey}Entry" must be a WritableSignal or Subject on ${token.name}.`,
          );
        }

        return [
          {
            bindingSource: bindingSource as EntryBindingSource<unknown>,
            serviceEntry,
          },
        ];
      },
    );

    if (!resolvedBindings.length) {
      return service;
    }

    const signalBindings = resolvedBindings.filter((binding): binding is SignalResolvedBinding => {
      const { bindingSource, serviceEntry } = binding;

      if (!isObservable(bindingSource)) {
        if (!isSignal(bindingSource)) {
          throw new Error(
            `toInject: binding source must be a Signal or Observable on ${token.name}.`,
          );
        }

        return true;
      }

      bindingSource.pipe(takeUntilDestroyed(destroyRef)).subscribe(nextValue => {
        setEntryValue(serviceEntry, nextValue);
      });

      return false;
    });

    if (!signalBindings.length) {
      return service;
    }

    effect(
      () => {
        signalBindings.forEach(({ bindingSource, serviceEntry }) => {
          const nextValue = bindingSource();
          untracked(() => {
            setEntryValue(serviceEntry, nextValue);
          });
        });
      },
      { injector },
    );

    return service;
  }

  return injectWithBindings;
}

function isWritableSignal(value: unknown): value is WritableSignal<unknown> {
  return typeof value === 'function' && 'set' in value && typeof value.set === 'function';
}

function isSignal(value: unknown): value is Signal<unknown> {
  return typeof value === 'function';
}

function isSubject(value: unknown): value is Subject<unknown> {
  return isObservable(value) && 'next' in value && typeof value.next === 'function';
}

function isEntryTarget(value: unknown): value is WritableSignal<unknown> | Subject<unknown> {
  return isWritableSignal(value) || isSubject(value);
}

function setEntryValue(entry: WritableSignal<unknown> | Subject<unknown>, value: unknown): void {
  if (isWritableSignal(entry)) {
    entry.set(value);
    return;
  }

  entry.next(value);
}
